<template>
	<div class="filter-bar-container">
		<HiddenHorizontalScrollbar class="filter-bar__provider-row">
			<div
				v-if="plusIconIsVisible && showAddButtonFirst"
				class="filter-bar__provider-icon filter-bar__provider-icon--plus"
			>
				<a @click.prevent="!isModalOpening && openProviderSelection()">
					<img
						:src="plusIcon"
						alt="Provider Selection"
						class="provider-icon"
						style="border: 1px solid rgba(255, 255, 255, 0.7)"
					/>
				</a>
			</div>
			<div class="filter-bar__provider-icon filter-bar__provider-icon--provider active" v-if="selectedProvider">
				<a
					:href="providerUrls[selectedProvider.shortName]"
					@click.prevent="providerClicked(selectedProvider.shortName)"
					class="selected-provider"
				>
					<ProviderIcon :providerShortName="selectedProvider.shortName" :lazy="false" />
				</a>
			</div>
			<template v-for="(provider, index) in userProviders">
				<div
					class="filter-bar__provider-icon filter-bar__provider-icon--provider"
					:key="provider.packageId"
					v-if="!isProviderActive(provider.shortName)"
				>
					<a :href="providerUrls[provider.shortName]" @click.prevent="providerClicked(provider.shortName)">
						<ProviderIcon
							:providerShortName="provider.shortName"
							:lazy="index >= imgProvidersBarLoadCount"
						/>
					</a>
				</div>
			</template>

			<div
				v-if="plusIconIsVisible && !showAddButtonFirst"
				class="filter-bar__provider-icon filter-bar__provider-icon--plus"
			>
				<a @click.prevent="!isModalOpening && openProviderSelection()">
					<img
						:src="plusIcon"
						alt="Provider Selection"
						class="provider-icon"
						style="border: 1px solid rgba(255, 255, 255, 0.7)"
					/>
				</a>
			</div>
		</HiddenHorizontalScrollbar>
		<div class="reset-button">
			<ResetButton />
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { PackagesSelectionModal } from '@/lazy-load-components';

import { CollectionType } from '@/enums/collection-type';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { calculateUrl } from '@/helpers/state-helper';

import Provider from '@/interfaces/provider';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';
import ResetButton from '@/components/buttons/ResetButton.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

type FilterBarPopularProviderProps = {
	addButtonVisible?: boolean;
	showAddButtonFirst?: boolean;
	collectionType: CollectionType;
	currentFilters: FilterCollection;
	providersClickable?: boolean;
	userProviders?: Provider[];
	updateRouteState?: boolean; // when toggling providers
	trackEvent?: boolean; // when toggling providers
	userSelectedPackages?: string[]; // used for sports provider until we have a better flagging mechanism in place
};

const props = withDefaults(defineProps<FilterBarPopularProviderProps>(), {
	addButtonVisible: true,
	providersClickable: true,
	userProviders: () => [],
	updateRouteState: true,
	trackEvent: true,
	userSelectedPackages: () => [],
});

const emits = defineEmits<{
	(
		e: 'toggle',
		toggleData: {
			filterName: string;
			value: string;
			selectedState: 0 | 1;
			updateRouteState: boolean;
			userSelectedPackages: string[];
			trackEvent: boolean;
		}
	): void;
}>();

const filterStore = useFilterStore();
const constantStore = useConstantStore();
const userStore = useUserStore();
const languageStore = useLanguageStore();

const router = useRouter();
const isModalOpening = ref(false);

const selectedProvider = computed(() => {
	return props.userProviders.find(el => el.shortName === props.currentFilters.providers?.[0]);
});

const { imgProvidersBarLoadCount } = useRuntimeConfig().public;

const plusIcon = `/${ASSETS_DIR}/img/plus_icon.png`;

const plusIconIsVisible = computed(() => {
	// causing issue with SSR > client hydration

	if (props.addButtonVisible) {
		return userStore.settings.providers?.length > 0;
	}
	return props.addButtonVisible;
});

const providerUrls = ref<Record<string, string>>({});

const _providersById = ref(constantStore.providersById);

// SSR perf: only call providersById once instead of every v-for loop.
watch(
	() => props.userProviders,
	() => {
		_providersById.value = constantStore.providersById;
	},
	{ immediate: true }
);

watch(
	() => languageStore.language,
	() => {
		// every call to a vuex getter seems to cost performance, that's why we only call it once before the loop
		const _providersByShortName = constantStore.providersByShortName;
		const _genresByShortName = constantStore.genresByShortName;
		// cached getters don't work in SSR, that's why we optimize it by putting it into data
		// disable subgenre parametric urls for better interlinking
		const { subgenres, ...filters } = props.currentFilters;
		void subgenres;

		providerUrls.value = props.userProviders.reduce((root, provider: Provider) => {
			const url = calculateUrl(
				router,
				undefined,
				props.collectionType,
				{ ...filters, providers: [provider.shortName] },
				_providersByShortName,
				_genresByShortName
			);

			if (url) root[provider.shortName] = url;

			return root;
		}, {} as Record<string, string>);
	},
	{ immediate: true }
);

async function providerClicked(providerName: string) {
	if (props.providersClickable) {
		const isActive: 1 | 0 = isFiltersContainsProvider(providerName) ? 0 : 1;
		const toggleData = {
			filterName: 'providers',
			value: providerName,
			selectedState: isActive,
			updateRouteState: props.updateRouteState,
			userSelectedPackages: props.userSelectedPackages,
			trackEvent: props.trackEvent,
		};

		const newRoute = await filterStore.toggle(toggleData as any);
		emits('toggle', toggleData);

		if (newRoute) router.push(newRoute);
	}
}
function isFiltersContainsProvider(providerName: string): boolean {
	return props.currentFilters.providers.includes(providerName);
}
function isProviderActive(providerName: string): boolean {
	return props.currentFilters.providers.length === 0 || isFiltersContainsProvider(providerName);
}

function onWillDismiss() {
	isModalOpening.value = false;
}

async function openProviderSelection() {
	isModalOpening.value = true;

	await ModalHelper.openModal(
		PackagesSelectionModal,
		{ providerPlace: ProviderFilterPlace.SETTINGS, onWillDismiss },
		{ cssClass: 'packages-selection-modal' },
		ProviderFilterPlace.SETTINGS,
		true
	);
	isModalOpening.value = false;
}
</script>

<style lang="scss" scoped>
.filter-bar-container {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.filter-bar {
	&__provider-row {
		margin-bottom: 5px;
	}

	&__provider-icon {
		display: inline-block;
		width: 50px;
		height: 50px;
		opacity: 0.25;
		margin: 10px 5px;
		transition: opacity 0.2s ease-in-out;

		&--plus .provider-icon {
			border-radius: 20%;
		}

		img {
			width: 100%;
		}

		&:first-child {
			margin-inline-start: 0;
		}

		&.active {
			opacity: 1;
			display: flex;
			flex-direction: row;
			align-items: center;
			width: auto;

			.selected-provider {
				width: 50px;
			}
		}
	}
}

.reset-button {
	margin-inline-start: 8px;
}
</style>
